import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { Link, NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { motion, useReducedMotion } from "framer-motion";
import {
  ArrowUpRight,
  CalendarDays,
  ChevronsUpDown,
  ClipboardList,
  FileText,
  Globe,
  Inbox,
  LayoutDashboard,
  LogOut,
  Menu,
  Moon,
  PanelLeftClose,
  PanelLeftOpen,
  Receipt,
  Search,
  Smartphone,
  Sun,
  Users,
} from "lucide-react";
import { useAuth } from "@/lib/auth";
import { api } from "@/lib/api";
import { ROLE, initials } from "@/lib/format";
import { useDriftTheme } from "@/lib/theme";
import { Mark } from "@/components/public/Logo";
import { CommandMenu } from "@/components/drift/CommandMenu";
import { NAV_SECTIONS, NAV } from "@/components/drift/nav";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Breadcrumb, BreadcrumbItem, BreadcrumbLink, BreadcrumbList, BreadcrumbPage, BreadcrumbSeparator } from "@/components/ui/breadcrumb";
import { cn } from "@/lib/utils";

/* ------------------------------------------------------------------
   Drift shell (2026)
   Sidebar on the page background; the work surface is an inset canvas
   with a sticky top bar (breadcrumb, ⌘K, theme, user). Sidebar can be
   collapsed to a 72px rail; the choice is remembered per browser.
   ------------------------------------------------------------------ */

export { NAV_SECTIONS, NAV };

const CRUMB_LABELS = {
  henvendingar: "Henvendingar",
  oppdrag: "Oppdrag",
  kundar: "Kundar og anlegg",
  anlegg: "Anlegg",
  planlegging: "Planlegging",
  tilbod: "Tilbod",
  fakturagrunnlag: "Fakturagrunnlag",
  montor: "Montørvisning",
  ai: "Assistent",
  felt: "Feltvising",
};

/* Pages can name the current record for the breadcrumb (e.g. an order ref). */
const CrumbContext = createContext({ crumb: null, setCrumb: () => {} });
export function usePageCrumb(label) {
  const { setCrumb } = useContext(CrumbContext);
  useEffect(() => {
    setCrumb(label || null);
    return () => setCrumb(null);
  }, [label, setCrumb]);
}

const RAIL_KEY = "ke_drift_rail";
const readRail = () => {
  try { return localStorage.getItem(RAIL_KEY) === "1"; } catch { return false; }
};

export const DriftWordmark = ({ className = "", compact = false, to = "/drift" }) => (
  <Link to={to} className={cn("inline-flex items-center gap-2.5 rounded-md font-display text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring", className)} data-testid="drift-wordmark" aria-label="Kvinnherad Elektro Drift">
    <Mark className="h-7 w-7" title="" aria-hidden="true" />
    {!compact && (
      <span className="text-[15.5px] leading-none tracking-[-0.02em]">
        <span className="font-semibold">Kvinnherad</span> <span className="font-medium text-foreground/70">Elektro</span>
        <span className="ml-2 rounded-[5px] bg-secondary px-1.5 py-0.5 font-mono text-[10.5px] font-medium text-muted-foreground">Drift</span>
      </span>
    )}
  </Link>
);

export const ThemeToggle = ({ theme, onToggle, className = "", testid = "drift-theme-toggle" }) => (
  <button
    type="button"
    onClick={onToggle}
    className={cn("inline-flex h-9 w-9 items-center justify-center rounded-lg text-muted-foreground transition-colors duration-150 hover:bg-accent/60 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring", className)}
    aria-label={theme === "dark" ? "Byt til lyst tema" : "Byt til mørkt tema"}
    aria-pressed={theme === "dark"}
    data-testid={testid}
    data-theme={theme}
  >
    {theme === "dark" ? <Sun className="h-4 w-4" strokeWidth={1.75} /> : <Moon className="h-4 w-4" strokeWidth={1.75} />}
  </button>
);

const Badge = ({ n, testid }) => (
  <span className="tabular inline-flex h-5 min-w-5 items-center justify-center rounded-md bg-background px-1.5 text-[11px] font-medium text-foreground ring-1 ring-border/80" data-testid={testid}>{n}</span>
);

function NavItem({ item, counts, rail, onNavigate }) {
  const location = useLocation();
  const Icon = item.icon;
  const badge = item.badge ? counts?.[item.badge] || 0 : 0;
  const alsoActive = (item.also || []).some((p) => location.pathname.startsWith(p));
  const link = (
    <NavLink
      to={item.to}
      end={item.end}
      onClick={onNavigate}
      data-testid={`drift-nav-${item.testid}`}
      className={({ isActive }) => {
        const on = isActive || alsoActive;
        return cn(
          "group relative flex h-9 items-center gap-3 rounded-lg px-2.5 text-[13.5px] font-medium transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
          on ? "bg-accent/70 text-foreground" : "text-muted-foreground hover:bg-accent/40 hover:text-foreground",
          rail && "justify-center px-0"
        );
      }}
    >
      {({ isActive }) => {
        const on = isActive || alsoActive;
        return (
          <>
            {on && <span className="absolute left-0 top-1/2 h-4 w-0.5 -translate-y-1/2 rounded-full bg-brand" aria-hidden />}
            <Icon className={cn("h-4 w-4 shrink-0 transition-colors duration-150", on ? "text-foreground" : "text-muted-foreground group-hover:text-foreground")} strokeWidth={1.75} aria-hidden />
            {!rail && <span className="flex-1 truncate">{item.label}</span>}
            {!rail && badge > 0 && <Badge n={badge} testid={`drift-nav-badge-${item.testid}`} />}
            {rail && badge > 0 && <span className="absolute right-1.5 top-1.5 h-1.5 w-1.5 rounded-full bg-brand" aria-hidden data-testid={`drift-nav-badge-${item.testid}`} />}
          </>
        );
      }}
    </NavLink>
  );
  if (!rail) return link;
  return (
    <Tooltip delayDuration={0}>
      <TooltipTrigger asChild>{link}</TooltipTrigger>
      <TooltipContent side="right" sideOffset={10} className="rounded-lg px-2.5 py-1.5 text-[12.5px]">
        {item.label}{badge > 0 && <span className="tabular ml-2 text-muted-foreground">{badge}</span>}
      </TooltipContent>
    </Tooltip>
  );
}

export function SideNav({ counts, rail = false, onNavigate }) {
  return (
    <nav className="flex flex-1 flex-col gap-5 overflow-y-auto px-3 pt-2" data-testid="drift-sidenav" aria-label="Drift">
      {NAV_SECTIONS.map((s) => (
        <div key={s.label}>
          {!rail && <p className="mb-1.5 px-2.5 text-[11.5px] font-medium text-muted-foreground/80">{s.label}</p>}
          {rail && <span className="mx-auto mb-1.5 block h-px w-6 bg-border" aria-hidden />}
          <div className="grid gap-0.5">
            {s.items.map((it) => <NavItem key={it.to} item={it} counts={counts} rail={rail} onNavigate={onNavigate} />)}
          </div>
        </div>
      ))}
    </nav>
  );
}

function UserMenu({ user, onLogout, onToggleTheme, theme, rail = false }) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className={cn("flex w-full items-center gap-3 rounded-xl p-2 text-left transition-colors duration-150 hover:bg-accent/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring", rail && "justify-center p-1.5")}
          data-testid="drift-user-menu"
          aria-label="Brukarmeny"
        >
          <span className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-foreground font-display text-[11.5px] font-semibold text-background">{initials(user?.name)}</span>
          {!rail && (
            <>
              <span className="min-w-0 flex-1">
                <span className="block truncate text-[13px] font-medium leading-tight" data-testid="drift-user-name">{user?.name}</span>
                <span className="block truncate text-[11.5px] text-muted-foreground">{user?.title || ROLE[user?.role]}</span>
              </span>
              <ChevronsUpDown className="h-3.5 w-3.5 text-muted-foreground" strokeWidth={1.75} aria-hidden />
            </>
          )}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent side="top" align="start" sideOffset={8} className="shadow-pop w-60 rounded-xl border-0 p-1.5">
        <DropdownMenuLabel className="px-2 py-1.5">
          <span className="block text-[13px] font-medium">{user?.name}</span>
          <span className="block text-[11.5px] font-normal text-muted-foreground">{user?.email}</span>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem asChild className="rounded-lg">
          <Link to="/drift/montor" data-testid="drift-user-menu-montor"><Smartphone className="mr-2 h-4 w-4" strokeWidth={1.75} /> Montørvisning</Link>
        </DropdownMenuItem>
        <DropdownMenuItem asChild className="rounded-lg">
          <Link to="/" data-testid="drift-public-site-link"><Globe className="mr-2 h-4 w-4" strokeWidth={1.75} /> kvinnheradelektro.no <ArrowUpRight className="ml-auto h-3.5 w-3.5 text-muted-foreground" /></Link>
        </DropdownMenuItem>
        <DropdownMenuItem onSelect={onToggleTheme} className="rounded-lg" data-testid="drift-user-menu-theme">
          {theme === "dark" ? <Sun className="mr-2 h-4 w-4" strokeWidth={1.75} /> : <Moon className="mr-2 h-4 w-4" strokeWidth={1.75} />}
          {theme === "dark" ? "Lyst tema" : "Mørkt tema"}
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem onSelect={onLogout} className="rounded-lg" data-testid="drift-user-menu-logout">
          <LogOut className="mr-2 h-4 w-4" strokeWidth={1.75} /> Logg ut
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function Crumbs({ crumb }) {
  const location = useLocation();
  const parts = location.pathname.split("/").filter(Boolean).slice(1); // drop "drift"
  const first = parts[0];
  const hasId = parts.length > 1;
  return (
    <Breadcrumb data-testid="drift-breadcrumb">
      <BreadcrumbList className="text-[13.5px] sm:gap-1.5">
        <BreadcrumbItem>
          {first ? <BreadcrumbLink asChild><Link to="/drift" className="transition-colors hover:text-foreground">Oversikt</Link></BreadcrumbLink> : <BreadcrumbPage className="font-medium">Oversikt</BreadcrumbPage>}
        </BreadcrumbItem>
        {first && (
          <>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              {hasId ? <BreadcrumbLink asChild><Link to={`/drift/${first === "anlegg" ? "kundar" : first}`} className="transition-colors hover:text-foreground">{CRUMB_LABELS[first] || first}</Link></BreadcrumbLink> : <BreadcrumbPage className="font-medium">{CRUMB_LABELS[first] || first}</BreadcrumbPage>}
            </BreadcrumbItem>
          </>
        )}
        {hasId && (
          <>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbPage className={cn("font-medium", !crumb && "font-mono text-[12.5px]")} data-testid="drift-breadcrumb-current">{crumb || "Detaljar"}</BreadcrumbPage>
            </BreadcrumbItem>
          </>
        )}
      </BreadcrumbList>
    </Breadcrumb>
  );
}

const isMac = typeof navigator !== "undefined" && /Mac|iPhone|iPad/.test(navigator.platform || "");

export function DriftLayout() {
  const { theme, toggle } = useDriftTheme();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const reduce = useReducedMotion();
  const [counts, setCounts] = useState({});
  const [open, setOpen] = useState(false);
  const [cmd, setCmd] = useState(false);
  const [rail, setRailState] = useState(readRail);
  const [crumb, setCrumb] = useState(null);
  const crumbValue = useMemo(() => ({ crumb, setCrumb }), [crumb]);

  const setRail = useCallback((v) => {
    setRailState(v);
    try { localStorage.setItem(RAIL_KEY, v ? "1" : "0"); } catch { /* ignore */ }
  }, []);

  // Counters for the sidebar badges. Quietly refreshed.
  useEffect(() => {
    let active = true;
    const load = () => api.get("/drift/dashboard").then((r) => {
      if (!active) return;
      const k = r.data?.kpis || {};
      setCounts({ inquiries: k.new_inquiries || 0, quotes: k.quotes_followup || 0, invoice: k.invoice_ready || 0 });
    }).catch(() => {});
    load();
    const t = setInterval(load, 45000);
    return () => { active = false; clearInterval(t); };
  }, [location.pathname]);

  // ⌘K / Ctrl+K opens the command menu.
  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setCmd((v) => !v);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  useEffect(() => { setOpen(false); }, [location.pathname]);

  const doLogout = () => { logout(); navigate("/drift/login"); };

  /* Workspace pages (the assistant) fill the canvas edge to edge and manage their own scrolling. */
  const bleed = location.pathname.startsWith("/drift/ai");

  return (
    <TooltipProvider>
    <CrumbContext.Provider value={crumbValue}>
    <div className="min-h-screen bg-background text-foreground" data-testid="drift-layout" data-theme={theme} data-rail={rail ? "true" : "false"}>
      {/* Sidebar */}
      <aside className={cn("fixed inset-y-0 left-0 z-40 hidden flex-col transition-[width] duration-300 ease-premium lg:flex", rail ? "w-[72px]" : "w-[272px]")} data-testid="drift-sidebar">
        <div className={cn("flex h-14 items-center", rail ? "justify-center px-0" : "justify-between pl-5 pr-3")}>
          <DriftWordmark compact={rail} />
          {!rail && (
            <Tooltip delayDuration={300}>
              <TooltipTrigger asChild>
                <button type="button" onClick={() => setRail(true)} className="inline-flex h-8 w-8 items-center justify-center rounded-lg text-muted-foreground transition-colors hover:bg-accent/60 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring" aria-label="Minimer sidepanelet" data-testid="drift-sidebar-collapse-button">
                  <PanelLeftClose className="h-4 w-4" strokeWidth={1.75} />
                </button>
              </TooltipTrigger>
              <TooltipContent side="right" className="rounded-lg text-[12.5px]">Minimer</TooltipContent>
            </Tooltip>
          )}
        </div>

        {/* Search / ⌘K */}
        <div className={cn("px-3 pb-1", rail && "px-3")}>
          <Tooltip delayDuration={0}>
            <TooltipTrigger asChild>
              <button
                type="button"
                onClick={() => setCmd(true)}
                className={cn("flex h-9 w-full items-center gap-2.5 rounded-lg border border-border/80 bg-card px-2.5 text-[13px] text-muted-foreground shadow-[0_1px_0_hsl(var(--border)/0.4)] transition-colors hover:border-foreground/25 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring", rail && "justify-center px-0")}
                data-testid="drift-command-palette-trigger"
                aria-label="Søk eller hopp til"
              >
                <Search className="h-4 w-4 shrink-0" strokeWidth={1.75} aria-hidden />
                {!rail && <span className="flex-1 truncate text-left">Søk eller hopp til …</span>}
                {!rail && <span className="kbd">{isMac ? "⌘" : "Ctrl"}K</span>}
              </button>
            </TooltipTrigger>
            {rail && <TooltipContent side="right" sideOffset={10} className="rounded-lg text-[12.5px]">Søk <span className="kbd ml-1.5">{isMac ? "⌘" : "Ctrl"}K</span></TooltipContent>}
          </Tooltip>
        </div>

        <SideNav counts={counts} rail={rail} />

        <div className={cn("mt-auto p-3", rail && "flex flex-col items-center gap-1")}>
          {rail && (
            <Tooltip delayDuration={0}>
              <TooltipTrigger asChild>
                <button type="button" onClick={() => setRail(false)} className="inline-flex h-9 w-9 items-center justify-center rounded-lg text-muted-foreground transition-colors hover:bg-accent/60 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring" aria-label="Utvid sidepanelet" data-testid="drift-sidebar-expand-button">
                  <PanelLeftOpen className="h-4 w-4" strokeWidth={1.75} />
                </button>
              </TooltipTrigger>
              <TooltipContent side="right" sideOffset={10} className="rounded-lg text-[12.5px]">Utvid</TooltipContent>
            </Tooltip>
          )}
          <div className={cn("flex items-center gap-1", rail ? "flex-col" : "mb-1 justify-between px-1")}>
            {!rail && <Link to="/" className="inline-flex h-8 items-center gap-1.5 rounded-lg px-2 text-[12px] text-muted-foreground transition-colors hover:bg-accent/60 hover:text-foreground" data-testid="drift-public-site-link-sidebar"><Globe className="h-3.5 w-3.5" strokeWidth={1.75} /> Nettsida</Link>}
            <ThemeToggle theme={theme} onToggle={toggle} className="h-8 w-8" />
            <button onClick={doLogout} className="inline-flex h-8 w-8 items-center justify-center rounded-lg text-muted-foreground transition-colors hover:bg-accent/60 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring" aria-label="Logg ut" data-testid="drift-logout-button"><LogOut className="h-4 w-4" strokeWidth={1.75} /></button>
          </div>
          <UserMenu user={user} onLogout={doLogout} onToggleTheme={toggle} theme={theme} rail={rail} />
        </div>
      </aside>

      {/* Mobile header */}
      <header className="sticky top-0 z-30 flex h-14 items-center justify-between border-b border-border/80 bg-background/90 px-4 backdrop-blur-xl lg:hidden" data-testid="drift-mobile-header">
        <DriftWordmark />
        <div className="flex items-center gap-1">
          <button type="button" onClick={() => setCmd(true)} className="inline-flex h-9 w-9 items-center justify-center rounded-lg text-muted-foreground hover:bg-accent/60 hover:text-foreground" aria-label="Søk" data-testid="drift-command-palette-trigger-mobile"><Search className="h-4 w-4" strokeWidth={1.75} /></button>
          <ThemeToggle theme={theme} onToggle={toggle} testid="drift-theme-toggle-mobile" />
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" className="rounded-lg" aria-label="Meny" data-testid="drift-mobile-menu-button"><Menu className="h-5 w-5" strokeWidth={1.75} /></Button>
            </SheetTrigger>
            <SheetContent side="left" className="flex w-[292px] flex-col bg-background p-0">
              <div className="flex h-14 items-center px-5"><DriftWordmark /></div>
              <SideNav counts={counts} onNavigate={() => setOpen(false)} />
              <div className="mt-auto border-t border-border p-3">
                <UserMenu user={user} onLogout={doLogout} onToggleTheme={toggle} theme={theme} />
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </header>

      {/* Canvas */}
      <div className={cn("transition-[padding] duration-300 ease-premium", rail ? "lg:pl-[72px]" : "lg:pl-[272px]")}>
        <div className="lg:py-2 lg:pr-2">
          <div className={cn("drift-canvas relative lg:rounded-2xl lg:border lg:border-border/80", bleed ? "flex h-[calc(100vh-3.5rem)] flex-col overflow-hidden lg:h-[calc(100vh-1rem)]" : "min-h-screen lg:min-h-[calc(100vh-1rem)]")} data-testid="drift-canvas" data-bleed={bleed ? "true" : "false"}>
            <header className="sticky top-14 z-20 hidden h-12 items-center justify-between gap-4 border-b border-border/70 bg-card/85 px-4 backdrop-blur-xl sm:px-6 lg:top-2 lg:flex lg:h-14 lg:rounded-t-2xl lg:px-8" data-testid="drift-topbar">
              <Crumbs crumb={crumb} />
              <div className="flex items-center gap-2 text-[12.5px] text-muted-foreground">
                <span className="hidden xl:inline">{user?.title || ROLE[user?.role]}</span>
              </div>
            </header>
            <main className={cn(bleed ? "min-h-0 flex-1" : "mx-auto w-full max-w-[1480px] px-4 py-6 sm:px-6 lg:px-8 lg:py-7")}>
              <motion.div
                key={bleed ? "ai" : location.pathname}
                className={cn(bleed && "h-full")}
                initial={reduce ? false : { opacity: 0, y: 6 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.24, ease: [0.22, 1, 0.36, 1] }}
              >
                <Outlet />
              </motion.div>
            </main>
          </div>
        </div>
      </div>

      <CommandMenu open={cmd} onOpenChange={setCmd} onToggleTheme={toggle} theme={theme} onLogout={doLogout} />
    </div>
    </CrumbContext.Provider>
    </TooltipProvider>
  );
}

export function MontorLayout() {
  const { theme, toggle } = useDriftTheme();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  return (
    <div className="min-h-screen bg-background text-foreground" data-testid="montor-layout" data-theme={theme}>
      <div className="mx-auto flex min-h-screen w-full max-w-[560px] flex-col bg-background sm:border-x sm:border-border">
        <header className="sticky top-0 z-30 flex h-14 items-center justify-between border-b border-border/80 bg-background/90 px-4 backdrop-blur-xl">
          <DriftWordmark to="/drift/montor" />
          <div className="flex items-center gap-1">
            {user?.role !== "montor" && (
              <Link to="/drift" className="mr-1 inline-flex h-8 items-center rounded-lg px-2 text-[12.5px] font-medium text-muted-foreground transition-colors hover:bg-accent/60 hover:text-foreground" data-testid="montor-back-to-office-link">Til kontor</Link>
            )}
            <ThemeToggle theme={theme} onToggle={toggle} testid="montor-theme-toggle" />
            <button onClick={() => { logout(); navigate("/drift/login"); }} className="ml-1 inline-flex h-9 w-9 items-center justify-center rounded-lg text-muted-foreground transition-colors hover:bg-accent/60 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring" aria-label="Logg ut" data-testid="montor-logout-button"><LogOut className="h-4 w-4" strokeWidth={1.75} /></button>
          </div>
        </header>
        <main className="flex-1 pb-28">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
